import { Badge, Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { camelCaseToTitleCase, FieldMapValue, rosCategories } from '@hb/shared'
import { SectionLabel } from '../../SectionLabel'

interface ROSItemValue {
  current?: boolean
  history?: boolean
  notes?: string
}

interface ROSCategoryValue {
  none?: boolean
  [key: string]: ROSItemValue | boolean | undefined
}

const ROSCategoryView = ({
  name,
  categoryData,
  items,
}: {
  name: string
  categoryData: ROSCategoryValue
  items: string[]
}) => {
  if (categoryData.none) {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600} color="gray.600">
          {name}
        </Text>
        <Text fontSize="sm" color="gray.500" fontStyle="italic">
          None
        </Text>
      </Box>
    )
  }

  const checkedItems = items.filter(itemKey => {
    const item = categoryData[itemKey] as ROSItemValue | undefined
    return item?.current || item?.history
  })

  if (checkedItems.length === 0) return null

  return (
    <Box>
      <Text fontSize="sm" fontWeight={600} color="gray.600" mb={1}>
        {name}
      </Text>
      <VStack align="flex-start" spacing={1}>
        {checkedItems.map(itemKey => {
          const item = categoryData[itemKey] as ROSItemValue
          const label = camelCaseToTitleCase(itemKey)
          const prefix =
            item.current && item.history
              ? 'Current & History'
              : item.current
                ? 'Current'
                : 'History of'
          return (
            <Box key={itemKey}>
              <Flex align="center" gap={2}>
                <Badge colorScheme="blue" fontSize="xs">
                  {prefix}
                </Badge>
                <Text fontSize="sm">{label}</Text>
              </Flex>
              {item.notes ? (
                <Text fontSize="xs" color="gray.600" ml={2} whiteSpace="pre-wrap">
                  {item.notes}
                </Text>
              ) : null}
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

export const ReviewOfSystemsView = ({ data }: { data: FieldMapValue }) => {
  const hasReviewOfSystems = data?.hasReviewOfSystems as boolean | undefined
  const reviewOfSystem = data?.reviewOfSystem as Record<string, ROSCategoryValue> | undefined

  if (!hasReviewOfSystems) {
    return (
      <Box w="100%">
        <SectionLabel>Review of Systems</SectionLabel>
        <Text fontStyle="italic" color="gray.500" fontSize="sm">
          No review of systems recorded
        </Text>
      </Box>
    )
  }

  const categoriesWithData = rosCategories.filter(cat => {
    const catData = reviewOfSystem?.[cat.key as string]
    if (!catData) return false
    if (catData.none) return true
    return cat.items.some(itemKey => {
      const item = catData[itemKey] as ROSItemValue | undefined
      return item?.current || item?.history
    })
  })

  return (
    <Box w="100%">
      <SectionLabel>Review of Systems</SectionLabel>
      {categoriesWithData.length === 0 ? (
        <Text fontStyle="italic" color="gray.500" fontSize="sm">
          No findings documented
        </Text>
      ) : (
        <VStack align="flex-start" spacing={3} divider={<Divider />}>
          {categoriesWithData.map(cat => {
            const catData = reviewOfSystem?.[cat.key as string]
            if (!catData) return null
            return (
              <ROSCategoryView
                key={cat.key as string}
                name={cat.name}
                categoryData={catData}
                items={cat.items}
              />
            )
          })}
        </VStack>
      )}
    </Box>
  )
}
